import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import logoLeft from './assets/logo-fenerbahce-arma.webp'; // SVG'deki maske telefonda pikselli işleniyordu; aynı tuvalden üretilmiş saydam WebP
import logoRight from './assets/logo-iskocya.png';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaXTwitter, FaInstagram, FaYoutube, FaTiktok, FaLinkedin, FaTelegram, FaBars, FaXmark, FaGlobe } from 'react-icons/fa6';
import { SOCIAL_LINKS, SOCIAL_LABELS } from './utils/socialLinks';
import AtaturkSignature from './components/AtaturkSignature';

// İkon ızgarasının tamamı Fenerbahçe SK'nın resmi hesaplarına gider (iletişim sayfasındaki
// düzenin aynısı). Derneğin kendi Instagram'ı mobil menünün altındaki adres bağlantısında.
const socialIcons = [
  { icon: <FaInstagram />, label: SOCIAL_LABELS.instagram, href: SOCIAL_LINKS.instagramFb },
  { icon: <FaFacebookF />, label: SOCIAL_LABELS.facebook, href: SOCIAL_LINKS.facebook },
  { icon: <FaXTwitter />, label: SOCIAL_LABELS.twitter, href: SOCIAL_LINKS.twitter },
  { icon: <FaYoutube />, label: SOCIAL_LABELS.youtube, href: SOCIAL_LINKS.youtube },
  { icon: <FaTiktok />, label: SOCIAL_LABELS.tiktok, href: SOCIAL_LINKS.tiktok },
  { icon: <FaLinkedin />, label: SOCIAL_LABELS.linkedin, href: SOCIAL_LINKS.linkedin },
  { icon: <FaTelegram />, label: SOCIAL_LABELS.telegram, href: SOCIAL_LINKS.telegram },
  { icon: <FaGlobe />, label: SOCIAL_LABELS.website, href: SOCIAL_LINKS.website },
];

const menuLinks = [
  { label: 'Anasayfa', to: '/' },
  { label: 'Derneğimiz', to: '/dernegimiz' },
  { label: 'Atatürk & Fenerbahçe', to: '/ataturk-fenerbahce' },
  { label: 'Maçlar', to: '/maclar' },
  { label: 'Üyelik', to: '/membership' },
  { label: 'İletişim', to: '/contact' },
   { label: 'Mağaza', to: '/store' },
  { label: 'Sponsorluk', to: '/sponsorluk' },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 640 : false);
  const [navHeight, setNavHeight] = useState(56);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const isCurrentlyScrolled = window.scrollY > 10;
      setIsScrolled(isCurrentlyScrolled);
    };
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
      if (navRef.current) {
        setNavHeight(navRef.current.getBoundingClientRect().height);
      }
    };
    handleResize();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <nav 
      ref={navRef}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        menuOpen || isScrolled || isMobile ? 'bg-[#00205b] shadow-lg' : 'bg-transparent backdrop-blur-[2px]'
      }`}
    >
      {/* Top Gradient Shadow */}
      <div className={`absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#0b1c35]/80 to-transparent pointer-events-none z-10 ${
        menuOpen || isScrolled || isMobile ? 'opacity-0' : 'opacity-100'
      } transition-opacity duration-300`}></div>

      <div className="max-w-7xl mx-auto flex items-center justify-between px-2 sm:px-4 py-1 relative z-20">
        {/* Sol Logo (eski sağ logo) */}
        <div className="flex items-center flex-shrink-0 min-w-[32px] sm:min-w-[36px] lg:min-w-[40px] z-50">
          <Link to="/" className="logo-container" aria-label="İskoçya Fenerbahçeliler Derneği Ana Sayfa">
            <img
              src={logoRight}
              alt="Fenerbahçe Eski Logo"
              className="logo-image logo-front h-8 w-8 sm:h-10 sm:w-10 lg:h-11 lg:w-11 transition-all duration-300"
              style={{ minWidth: 32, width: '100%', height: '100%' }}
              width="44"
              height="44"
              loading="eager"
              fetchpriority="high"
            />
            <img
              src={logoLeft}
              alt="İskoçya Fenerbahçeliler Derneği Logo"
              className="logo-image logo-back logo-left h-8 w-8 sm:h-10 sm:w-10 lg:h-11 lg:w-11 transition-all duration-300"
              style={{ minWidth: 32, width: '100%', height: '100%' }}
              width="44"
              height="44"
              loading="eager"
              fetchpriority="high"
            />
          </Link>
        </div>

        {/* Menü 8 öğeye çıkınca 1024px'te taşıyordu: orta blok min-w-0 olmadan
            daralamıyor, iki yandaki logoları ve sağ alanı sıkıştırıyordu.
            Yazı ve boşluklar ekran genişliğine göre kademeli. */}
        <nav className="hidden lg:flex flex-1 min-w-0 justify-center px-1" aria-label="Ana Navigasyon">
          <ul className="flex items-center gap-2.5 xl:gap-3.5 2xl:gap-5">
            {menuLinks.map((item, i) => (
              <li key={i}>
                <Link to={item.to} className="text-white font-medium text-[13px] xl:text-[15px] 2xl:text-base hover:text-yellow-400 transition whitespace-nowrap">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobil Hamburger Menü */}
        <button
          className={`lg:hidden ml-auto relative flex items-center justify-center text-white text-lg focus:outline-none z-50 rounded-full w-8 h-8 ${
            menuOpen || isScrolled ? 'bg-[#00205b]' : 'bg-transparent'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menüyü Aç/Kapat"
        >
          {menuOpen ? <FaXmark className="transition-transform duration-300 scale-110" /> : <FaBars className="transition-transform duration-300" />}
        </button>

        {/* Sağ Alan */}
        <div className="flex items-center flex-shrink-0 gap-1 sm:gap-2 lg:gap-2 xl:gap-3 z-50">
          {/* <button className="hidden md:block bg-[#ffe600] text-[#00205b] font-medium rounded-lg px-2 lg:px-3 xl:px-4 py-1 shadow-md hover:bg-white transition text-xs lg:text-sm whitespace-nowrap">
            Üye Ol
          </button> */}
          {/* Sosyal Medya İkonları - Desktop */}
          <div className="hidden xl:flex gap-1.5">
            {socialIcons.slice(0, 4).map((item, i) => (
              <a
                key={i}
                href={item.href}
                title={item.label}
                aria-label={item.label}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-white rounded-full w-6 h-6 md:w-4 md:h-4  lg:w-5 lg:h-5 xl:w-8 xl:h-8 flex items-center justify-center text-xs lg:text-xs xl:text-sm hover:bg-[#ffe600] hover:text-[#00205b] transition ${
                  isScrolled ? 'bg-[#19398a]' : 'bg-black/30'
                }`}
              >
                {item.icon}
              </a>
            ))}
          </div>
          {/* Dil Seçici - Desktop */}
          <div className={`hidden lg:flex items-center rounded-lg px-1.5 py-0.5 cursor-pointer hover:bg-[#ffe600] transition-colors group ${
            isScrolled ? 'bg-[#19398a]' : 'bg-black/30'
          }`}>
            <FaGlobe className="text-white mr-1 group-hover:text-[#00205b] text-xs" />
            <span className="text-white text-xs font-medium group-hover:text-[#00205b]">TR</span>
          </div>
          {/* Sağ Logo (eski sol logo) sadece md ve üstünde göster */}
          <div className="hidden lg:flex items-center flex-shrink-0 min-w-[40px] ml-1 z-50">
             <div className="logo-container">
              <img
                src={logoLeft}
                alt="Fenerbahçe Logo"
                className="logo-image logo-front logo-left h-8 w-8 sm:h-10 sm:w-10 lg:h-11 lg:w-11 transition-all duration-300"
                style={{ minWidth: 32, width: '100%', height: '100%' }}
                width="44"
                height="44"
                loading="eager"
                fetchpriority="high"
              />
              <img
                src={logoRight}
                alt="Fenerbahçe Eski Logo"
                className="logo-image logo-back h-8 w-8 sm:h-10 sm:w-10 lg:h-11 lg:w-11 transition-all duration-300"
                style={{ minWidth: 32, width: '100%', height: '100%' }}
                width="44"
                height="44"
                loading="eager"
                fetchpriority="high"
              />
            </div>
          </div>
        </div>

        {/* Mobil Menü İçeriği */}
        <div
          className={`mobile-menu-panel fixed left-0 right-0 bottom-0 z-40 transition-all duration-500 lg:hidden ${
            menuOpen ? 'opacity-100 visible text-white' : 'opacity-0 invisible'
          }`}
          style={{ top: menuOpen ? navHeight : 0 }}
        >
          <div className={`mobile-menu-inner transition-all duration-500 ${
            menuOpen ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <ul className="flex flex-col gap-5 w-full items-center">
              {menuLinks.map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="text-white text-xl font-semibold hover:text-[#ffe600] transition"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <Link
              to="/membership"
              className="bg-[#ffe600] text-[#00205b] font-medium rounded-lg px-8 py-2 shadow-md hover:bg-[#00205b] hover:text-white transition text-lg w-64 text-center mt-5"
              onClick={() => setMenuOpen(false)}
            >
              Üye Ol
            </Link>

            {/* İnce Çizgi */}
            <div className="w-32 h-px bg-white/25 mt-5"></div>

            {/* Mobil Dil Seçici */}
            <div className="bg-[#19398a] rounded-lg px-3 py-1.5 cursor-pointer hover:bg-[#ffe600] transition-colors group flex items-center mt-4">
              <FaGlobe className="text-white mr-2 group-hover:text-[#00205b]" />
              <span className="text-white text-sm font-medium group-hover:text-[#00205b]">TR</span>
            </div>

            {/* İnce Çizgi */}
            <div className="w-32 h-px bg-white/25 mt-4"></div>

            <div className="grid grid-cols-4 gap-2 px-4 mt-4 max-w-xs">
              {socialIcons.map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  title={item.label}
                  aria-label={item.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#19398a] text-white rounded-full w-7 h-7 flex items-center justify-center text-xs hover:bg-[#ffe600] hover:text-[#00205b] transition"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.icon}
                </a>
              ))}
            </div>

            {/* Derneğin Instagram adresi */}
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center gap-2 text-[#fdd023] text-sm font-semibold hover:text-white transition"
              onClick={() => setMenuOpen(false)}
            >
              <FaInstagram aria-hidden="true" />
              <span>@fenerbahce_iskocya</span>
            </a>

            {/* En altta Atatürk imzası — footer'daki gibi soldan sağa yazılır.
                Menü her açılışta baştan çizilsin diye key'e menuOpen veriliyor. */}
            {menuOpen && (
              <div className="mobile-menu-signature">
                <AtaturkSignature key="mobile-menu-signature" />
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 